import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

/**
 * Elicitation request from the server (order confirmation, etc.)
 */
export interface ElicitationRequest {
  id: string
  mode: 'form' | 'url'
  message: string
  requestedSchema?: Record<string, unknown>
  url?: string
}

/**
 * Order operation type
 */
export type OrderOperationType = 'submit' | 'modify' | 'delete'

/**
 * Parsed order details from elicitation message
 */
export interface OrderDetails {
  operationType: OrderOperationType
  // Common fields
  stock?: string
  stockName?: string
  refNo?: string
  // Submit order fields
  direction?: string
  amount?: number
  price?: number
  orderType?: string
  validity?: string
  // Modify order fields
  modifyType?: string
  newPrice?: number
  oldPrice?: number
  newAmount?: number
  oldAmount?: number
}

export const useElicitationStore = defineStore('elicitation', () => {
  // Current pending elicitation request
  const currentRequest = ref<ElicitationRequest | null>(null)
  
  // Show/hide the confirmation modal
  const showModal = ref(false)
  
  // Parsed order details for display
  const orderDetails = ref<OrderDetails | null>(null)
  
  // Loading state while sending response
  const isResponding = ref(false)

  // WebSocket send function (set by websocket service)
  let sendResponse: ((elicitationId: string, action: string, content?: Record<string, unknown>) => void) | null = null

  const hasPendingRequest = computed(() => currentRequest.value !== null)

  /**
   * Set the WebSocket response sender function
   */
  function setSendResponseFn(fn: (elicitationId: string, action: string, content?: Record<string, unknown>) => void): void {
    sendResponse = fn
  }

  /**
   * Handle incoming elicitation request from WebSocket
   */
  function handleElicitationRequest(request: ElicitationRequest): void {
    currentRequest.value = request
    
    // Parse order details from message
    orderDetails.value = parseOrderMessage(request.message)
    
    // Show the modal
    showModal.value = true
  }

  /**
   * Detect operation type from message header
   */
  function detectOperationType(message: string): OrderOperationType {
    if (message.includes('Emir İptali Onayı') || message.includes('Order Cancellation')) {
      return 'delete'
    }
    if (message.includes('Emir Değişikliği Onayı') || message.includes('Order Modification')) {
      return 'modify'
    }
    return 'submit'
  }

  /**
   * Parse order details from the elicitation message
   * 
   * Submit Order format:
   * Emir Onayı / Order Confirmation
   * ─────────────────────────────
   * Hisse / Stock: GSRAY (E)
   * İşlem / Direction: Alım (Buy)
   * Miktar / Amount: 1000 lot
   * Fiyat / Price: 1.01
   * Emir Tipi / Order Type: Piyasa
   * Geçerlilik / Validity: Günlük
   * 
   * Modify Order format:
   * Emir Değişikliği Onayı / Order Modification Confirmation
   * ─────────────────────────────
   * Referans No / Ref No: 123456
   * Değişiklik Tipi / Modify Type: Fiyat Değişikliği
   * Eski Fiyat / Old Price: 1.00
   * Yeni Fiyat / New Price: 1.05
   * Emir Tipi / Order Type: Limit
   * Geçerlilik / Validity: Günlük
   * 
   * Delete Order format:
   * Emir İptali Onayı / Order Cancellation Confirmation
   * ─────────────────────────────
   * Referans No / Ref No: 123456
   * İşlem / Operation: OrderDelete
   */
  function parseOrderMessage(message: string): OrderDetails | null {
    try {
      const operationType = detectOperationType(message)
      const lines = message.split('\n')
      const details: Partial<OrderDetails> = { operationType }

      for (const line of lines) {
        // Common: Referans No / Ref No
        if (line.includes('Referans No') || line.includes('Ref No:')) {
          const match = line.match(/(?:Referans No|Ref No)[^:]*:\s*(.+)/)
          if (match) {
            details.refNo = match[1].trim()
          }
        }
        // Submit: Hisse / Stock
        else if (line.includes('Hisse / Stock:')) {
          const match = line.match(/Hisse \/ Stock:\s*(.+)/)
          if (match) {
            const stockPart = match[1].trim()
            const stockMatch = stockPart.match(/^(\w+)\s*\(([^)]+)\)/)
            if (stockMatch) {
              details.stock = stockMatch[1]
              details.stockName = stockMatch[2]
            } else {
              details.stock = stockPart
            }
          }
        }
        // Submit: İşlem / Direction
        else if (line.includes('İşlem / Direction:')) {
          const match = line.match(/İşlem \/ Direction:\s*(.+)/)
          if (match) {
            details.direction = match[1].trim()
          }
        }
        // Submit/Modify: Miktar / Amount (for submit) or Yeni Miktar / New Amount (for modify)
        else if (line.includes('Yeni Miktar') || line.includes('New Amount')) {
          const match = line.match(/(?:Yeni Miktar|New Amount)[^:]*:\s*(\d+)/)
          if (match) {
            details.newAmount = parseInt(match[1], 10)
          }
        }
        else if (line.includes('Eski Miktar') || line.includes('Old Amount')) {
          const match = line.match(/(?:Eski Miktar|Old Amount)[^:]*:\s*(\d+)/)
          if (match) {
            details.oldAmount = parseInt(match[1], 10)
          }
        }
        else if (line.includes('Miktar / Amount:')) {
          const match = line.match(/Miktar \/ Amount:\s*(\d+)/)
          if (match) {
            details.amount = parseInt(match[1], 10)
          }
        }
        // Submit/Modify: Fiyat / Price (for submit) or Yeni Fiyat / New Price (for modify)
        else if (line.includes('Yeni Fiyat') || line.includes('New Price')) {
          const match = line.match(/(?:Yeni Fiyat|New Price)[^:]*:\s*([\d.]+)/)
          if (match) {
            details.newPrice = parseFloat(match[1])
          }
        }
        else if (line.includes('Eski Fiyat') || line.includes('Old Price')) {
          const match = line.match(/(?:Eski Fiyat|Old Price)[^:]*:\s*([\d.]+)/)
          if (match) {
            details.oldPrice = parseFloat(match[1])
          }
        }
        else if (line.includes('Fiyat / Price:')) {
          const match = line.match(/Fiyat \/ Price:\s*([\d.]+)/)
          if (match) {
            details.price = parseFloat(match[1])
          }
        }
        // Modify: Değişiklik Tipi / Modify Type
        else if (line.includes('Değişiklik Tipi') || line.includes('Modify Type')) {
          const match = line.match(/(?:Değişiklik Tipi|Modify Type)[^:]*:\s*(.+)/)
          if (match) {
            details.modifyType = match[1].trim()
          }
        }
        // Common: Emir Tipi / Order Type
        else if (line.includes('Emir Tipi / Order Type:')) {
          const match = line.match(/Emir Tipi \/ Order Type:\s*(.+)/)
          if (match) {
            details.orderType = match[1].trim()
          }
        }
        // Common: Geçerlilik / Validity
        else if (line.includes('Geçerlilik / Validity:')) {
          const match = line.match(/Geçerlilik \/ Validity:\s*(.+)/)
          if (match) {
            details.validity = match[1].trim()
          }
        }
      }

      return details as OrderDetails
    } catch {
      return null
    }
  }

  /**
   * User confirms the order
   */
  function confirm(): void {
    confirmWithContent({})
  }

  /**
   * User confirms the order with optional edited content
   */
  function confirmWithContent(content: Record<string, unknown>): void {
    if (!currentRequest.value || !sendResponse) return

    isResponding.value = true
    sendResponse(currentRequest.value.id, 'accept', content)
    
    // Close and reset
    closeModal()
  }

  /**
   * User declines the order
   */
  function decline(): void {
    if (!currentRequest.value || !sendResponse) return

    isResponding.value = true
    sendResponse(currentRequest.value.id, 'decline')
    
    // Close and reset
    closeModal()
  }

  /**
   * User cancels (closes modal without action)
   */
  function cancel(): void {
    if (!currentRequest.value || !sendResponse) return

    sendResponse(currentRequest.value.id, 'cancel')
    
    // Close and reset
    closeModal()
  }

  /**
   * Close the modal and reset state
   */
  function closeModal(): void {
    showModal.value = false
    currentRequest.value = null
    orderDetails.value = null
    isResponding.value = false
  }

  /**
   * Reset on user logout
   */
  function reset(): void {
    closeModal()
    sendResponse = null
  }

  return {
    currentRequest,
    showModal,
    orderDetails,
    isResponding,
    hasPendingRequest,
    setSendResponseFn,
    handleElicitationRequest,
    confirm,
    confirmWithContent,
    decline,
    cancel,
    closeModal,
    reset
  }
})
